import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Avatar from '../components/Avatar';
import ProfileCard, { cardBackground } from '../components/ProfileCard';
import Modal, { ModalButton } from '../dm/Modal';
import { Icons } from '../dm/Menu';
import { displayName } from '../dm/format';
import { useAuth } from '../AuthContext';
import { useServer, useServers } from './ServersContext';
import { ROLE_LABEL, canDo, outranks } from './format';

const RANK = { owner: 0, admin: 1, moderator: 2, member: 3 };
const OWN_STATUS = { online: 'online', away: 'away', dnd: 'dnd', invisible: undefined };

// Right column on a server: members grouped by online / offline, sorted by role.
// Online here means "connected to this server right now".
export default function ServerMembers({ nodeId, onClose }) {
  const { user, nicknames } = useAuth();
  const { ensureMembers } = useServers();
  const { st } = useServer(nodeId);
  const [profileOf, setProfileOf] = useState(null);

  useEffect(() => { if (st.status === 'ready') ensureMembers(nodeId); }, [st.status, nodeId, ensureMembers]);

  const statusOf = (m) => (m.id === user?.id ? OWN_STATUS[user.status_preference || 'online'] : st.presence[m.id]);
  const sorted = [...(st.members || [])].sort((a, b) => RANK[a.role] - RANK[b.role] || displayName(a, nicknames).localeCompare(displayName(b, nicknames)));
  const online = sorted.filter(m => statusOf(m));
  const offline = sorted.filter(m => !statusOf(m));

  const row = (m, dim) => (
    <button
      key={m.id}
      type="button"
      onClick={() => setProfileOf(m)}
      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-sm border-0 bg-transparent px-2 py-1.5 text-left hover:bg-surface-2 ${dim ? 'opacity-50 hover:opacity-100' : ''}`}
    >
      <Avatar src={m.avatar} size={32} border="none" status={statusOf(m)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="truncate font-display text-sm font-bold text-text">{displayName(m, nicknames)}</span>
          {m.role === 'owner' && <span className="shrink-0 text-amber" title="Owner">{Icons.crown}</span>}
        </span>
        {m.role !== 'member' && <span className="block truncate text-[11px] text-text-dim">{ROLE_LABEL[m.role]}</span>}
        {m.timeout_until && new Date(m.timeout_until) > new Date() && <span className="block truncate text-[11px] text-amber">Timed out</span>}
      </span>
    </button>
  );

  return (
    <aside className="flex h-full w-panel max-w-[85vw] shrink-0 flex-col bg-surface pb-[var(--safe-bottom)] pt-[var(--safe-top)] md:pt-0">
      <div className="flex h-12 shrink-0 items-center border-0 border-b border-solid border-black px-4">
        <span className="flex-1 font-display text-[15px] font-extrabold text-text">Members</span>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-text-muted hover:bg-surface-2 hover:text-text">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-3">
        {!st.members ? (
          <div className="flex justify-center py-8"><div className="h-5 w-5 animate-spin rounded-full border-[3px] border-solid border-border border-t-purple" /></div>
        ) : (
          <>
            <div className="px-2 pb-1 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">Online - {online.length}</div>
            {online.map(m => row(m, false))}
            {offline.length > 0 && <div className="mt-4 px-2 pb-1 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">Offline - {offline.length}</div>}
            {offline.map(m => row(m, true))}
          </>
        )}
      </div>
      {profileOf && (
        <MemberProfile
          nodeId={nodeId}
          member={(st.members || []).find(m => m.id === profileOf.id) || profileOf}
          status={statusOf(profileOf)}
          onClose={() => setProfileOf(null)}
        />
      )}
    </aside>
  );
}

// BLOBL profile of a member, from the Core (not from the node), plus moderation for those allowed
function MemberProfile({ nodeId, member, status, onClose }) {
  const navigate = useNavigate();
  const { user, nicknames } = useAuth();
  const [profile, setProfile] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios.get(`/api/users/${encodeURIComponent(member.username)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => (r.data?.id === member.id ? setProfile(r.data) : setMissing(true)))
      .catch(() => setMissing(true));
  }, [member.id, member.username]);

  const isMe = member.id === user?.id;
  return (
    <Modal title={displayName(member, nicknames)} onClose={onClose}>
      {missing && <div className="py-4 text-sm text-text-muted">This profile is not available.</div>}
      {!profile && !missing && <div className="flex justify-center py-8"><div className="h-5 w-5 animate-spin rounded-full border-[3px] border-solid border-border border-t-purple" /></div>}
      {profile && (
        <div className="-mx-5 -mb-4 overflow-hidden" style={{ background: cardBackground(profile) }}>
          <ProfileCard
            profile={profile}
            nickname={nicknames[profile.id]}
            status={status}
            actions={!isMe && (
              <button type="button" onClick={() => navigate(`/dm/user/${profile.id}`)} className="cursor-pointer rounded-pill border-0 bg-brand px-4 py-2 font-display text-sm font-bold text-white">
                Message
              </button>
            )}
          />
        </div>
      )}
      {!isMe && <ModerationActions nodeId={nodeId} member={member} onDone={onClose} />}
    </Modal>
  );
}

const TIMEOUTS = [
  { minutes: 1, label: '1 minute' }, { minutes: 5, label: '5 minutes' }, { minutes: 10, label: '10 minutes' },
  { minutes: 60, label: '1 hour' }, { minutes: 1440, label: '1 day' }, { minutes: 10080, label: '1 week' },
];
const btn = 'cursor-pointer rounded-pill border-0 px-3 py-1.5 font-display text-xs font-bold disabled:opacity-50';

// Role, timeout, kick and ban. Shown only when allowed and the member's role is lower than mine.
function ModerationActions({ nodeId, member, onDone }) {
  const { conn } = useServers();
  const { st } = useServer(nodeId);
  const myRole = st.me?.role;
  const [panel, setPanel] = useState(null); // 'timeout' | 'kick' | 'ban'
  const [minutes, setMinutes] = useState(10);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!outranks(myRole, member.role)) return null;
  const canRole = canDo(myRole, 'manage_moderators');
  const canTimeout = canDo(myRole, 'timeout_members');
  const canKick = canDo(myRole, 'kick_members');
  const canBan = canDo(myRole, 'ban_members');
  if (!canRole && !canTimeout && !canKick && !canBan) return null;
  // Owners can appoint admins; admins only moderators
  const roles = canDo(myRole, 'manage_admins') ? ['admin', 'moderator', 'member'] : ['moderator', 'member'];
  const timedOut = member.timeout_until && new Date(member.timeout_until) > new Date();

  const run = async (fn, close = false) => {
    setBusy(true); setError('');
    try {
      await fn(conn(nodeId));
      setPanel(null); setReason('');
      if (close) onDone();
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border-0 border-t border-solid border-border pt-3">
      <div className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">Moderation</div>
      {canRole && (
        <label className="mb-3 flex items-center gap-2 text-sm text-text-muted">
          Role
          <select
            value={member.role}
            disabled={busy}
            onChange={e => run(c => c.patch(`/api/members/${member.id}`, { role: e.target.value }))}
            className="flex-1 cursor-pointer rounded border border-solid border-border bg-surface px-2 py-1.5 font-body text-sm text-text outline-none"
          >
            {roles.includes(member.role) ? null : <option value={member.role}>{ROLE_LABEL[member.role]}</option>}
            {roles.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        {canTimeout && (timedOut
          ? <button type="button" disabled={busy} className={`${btn} bg-surface-3 text-text`} onClick={() => run(c => c.del(`/api/members/${member.id}/timeout`))}>Remove timeout</button>
          : <button type="button" className={`${btn} bg-surface-3 text-amber`} onClick={() => setPanel(panel === 'timeout' ? null : 'timeout')}>Timeout</button>)}
        {canKick && <button type="button" className={`${btn} bg-surface-3 text-red`} onClick={() => setPanel(panel === 'kick' ? null : 'kick')}>Kick</button>}
        {canBan && <button type="button" className={`${btn} bg-red text-white`} onClick={() => setPanel(panel === 'ban' ? null : 'ban')}>Ban</button>}
      </div>
      {timedOut && <div className="mt-2 text-xs text-amber">Timed out until {new Date(member.timeout_until).toLocaleString()}</div>}

      {panel && (
        <div className="mt-3 flex flex-col gap-2 rounded bg-surface px-3 py-3">
          <div className="text-sm text-text-muted">
            {panel === 'timeout' && 'They can still read, but not write, upload or invite.'}
            {panel === 'kick' && 'They are removed from the server and can come back with an invite.'}
            {panel === 'ban' && 'They are removed from the server and can\'t join again until you unban them.'}
          </div>
          {panel === 'timeout' && (
            <select value={minutes} onChange={e => setMinutes(Number(e.target.value))} className="cursor-pointer rounded border border-solid border-border bg-surface-2 px-2 py-1.5 font-body text-sm text-text outline-none">
              {TIMEOUTS.map(t => <option key={t.minutes} value={t.minutes}>{t.label}</option>)}
            </select>
          )}
          {panel !== 'kick' && (
            <input value={reason} maxLength={500} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)"
              className="rounded border border-solid border-border bg-surface-2 px-3 py-2 font-body text-sm text-text outline-none placeholder:text-text-dim focus:border-purple" />
          )}
          <div className="flex gap-2">
            <ModalButton onClick={() => setPanel(null)}>Cancel</ModalButton>
            <ModalButton danger disabled={busy} onClick={() => {
              if (panel === 'timeout') run(c => c.post(`/api/members/${member.id}/timeout`, { minutes, reason }));
              if (panel === 'kick') run(c => c.del(`/api/members/${member.id}`), true);
              if (panel === 'ban') run(c => c.post('/api/bans', { user_id: member.id, reason }), true);
            }}>
              {busy ? '…' : panel === 'timeout' ? 'Time out' : panel === 'kick' ? 'Kick' : 'Ban'}
            </ModalButton>
          </div>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red">{error}</div>}
    </div>
  );
}
